/**
 * ONE TAP — the renderer.
 *
 * Owns the WebGL context, the scene, the camera, the lighting rig, the sky and the post
 * chain. The rest of the client treats it as a black box: set the camera transform, call
 * `render(dt)`, and poke the feel channels (`setSpeed01`, `addShake`, `setCameraRoll`,
 * `setTargetFov`, `inkSplash`) as events happen.
 *
 * Colour pipeline: everything renders linear into a half-float buffer with tone mapping OFF,
 * and the sunlit composite does the high-key tonemap, the grade and the sRGB encode in one
 * place. That is the only way the bloom, the sun shafts, the speed effects and the kill splash
 * all agree about what "white" means — and it is what lets toon.ts author its value ladder in
 * CIE L* and invert the whole chain to get there.
 *
 * Quality: three tiers, and by default the renderer watches its own frame time and steps
 * down on its own. It never steps below "outlines + hard bands + bloom", because those three
 * things ARE the game's readability. What it gives up first is ambient occlusion, the radial
 * speed blur and shadow resolution.
 */

import * as THREE from 'three';
import {
  CAMERA_ROLL_DAMP,
  FOV_BASE,
  FOV_DAMP_RATE,
  PALETTE,
  SHADOW_MAP_SIZE,
} from '@shared/constants';
import { clamp, damp } from '@shared/math';
import { createLighting, type LightingRig } from './lighting';
import { createSky, type Sky } from './sky';
import { createPostFX, type PostFX, type RenderQuality } from './postfx';
import {
  disposeToonShared,
  setOutlineResolution,
  setToonPixelRatio,
  setToonShadowFade,
  setToonSunDirection,
  updateToonTime,
} from './toon';

export type { RenderQuality } from './postfx';

/** Trauma -> angle. 1.0 of trauma is a ~5.7 degree kick, which is a big hit. */
const SHAKE_MAX_ANGLE = 0.1;
const SHAKE_MAX_OFFSET = 0.055;
/** Exponential trauma decay; ~0.09s half-life so even a small tap reads for a few frames. */
const SHAKE_DECAY = 8.0;
/** Perceptual curve on trauma. Slightly super-linear so big hits dominate small ones. */
const SHAKE_CURVE = 1.35;
const SHAKE_FREQ = 27.0;

/**
 * AERIAL PERSPECTIVE, AND WHERE IT IS ALLOWED TO EXIST.
 *
 * DESIGN.md 4 wants distant towers fading toward the sky, and it also wants the play space left
 * alone: fog is a lerp toward a bright horizon colour applied to EVERY surface, so any of it
 * over the arena lifts distant decks and soffits toward white and collapses the value ladder at
 * exactly the range a player is trying to read a rooftop. An earlier pass ran 70m/340m and lost
 * fifteen display points at the far wall to it.
 *
 * So the near plane is pushed out past the arena's own longest sightline. A rooftop complex is
 * a couple of hundred metres of playable ground at most; at 220m nothing a player can shoot is
 * fogged at all, and the ramp out to 1200m is spent entirely on backdrop geometry — a tower at
 * 300m is 8% hazed, at 600m 39%, at 1000m 80%. That is a real depth cue on the city and exactly
 * zero cost to the value ladder over the decks.
 *
 * The camera's far plane sits beyond FOG_FAR so a tower at the fog's far end dissolves into
 * haze rather than popping through a clip plane. 0.05..1500 is a 30000:1 depth range, which a
 * 24-bit buffer carries without z-fighting at the scales this arena works in.
 */
const FOG_NEAR = 220;
const FOG_FAR = 1200;
const CAMERA_FAR = 1500;

const MAX_DPR = 2;
const DPR_BY_QUALITY: Record<RenderQuality, number> = { low: 1, medium: 1.5, high: MAX_DPR };
/**
 * Shadow map resolution per tier, and SHADOWS ARE NEVER FULLY DISABLED AT ANY OF THEM.
 *
 * The cascade shrank to 176m across with the move outdoors (lighting.ts), which buys back a
 * factor of two on the texel at every tier: 43mm on high, 86mm on medium, 172mm on low. On a
 * rooftop the cast shadow is the frame's subject — a parapet lays a metre-and-a-half band
 * across the deck behind it — so that crispness is the difference between a shadow with a shape
 * and a smudge.
 *
 * `low` used to be 0. That was wrong, and it cost two full review rounds: the auto-quality
 * governor drops to `low` on a slow machine (or in an automated browser tab, which composites
 * in software), which silently turned every cast shadow off. A visual reviewer then correctly
 * reported "there is not a single cast shadow anywhere" while the lighting code was in fact
 * correct — it was judging a code path where shadows cannot exist.
 *
 * Beyond the review confusion, 0 is wrong on its own terms. In Sunlit Cel the cast shadow is
 * the PRIMARY depth cue (DESIGN.md §4): without it the arena has no value structure and objects
 * float. A low-end machine should get cheaper shadows, never no shadows — degrade the map size,
 * never the phenomenon.
 */
const SHADOW_SIZE_BY_QUALITY: Record<RenderQuality, number> = {
  low: SHADOW_MAP_SIZE / 2,
  medium: SHADOW_MAP_SIZE,
  high: SHADOW_MAP_SIZE * 2,
};
const QUALITY_ORDER: RenderQuality[] = ['low', 'medium', 'high'];

/** Read a `?quality=` pin from the URL. Returns null when absent or unrecognised. */
function readPinnedQuality(): RenderQuality | null {
  if (typeof location === 'undefined') return null;
  try {
    const q = new URLSearchParams(location.search).get('quality');
    return q && (QUALITY_ORDER as string[]).includes(q) ? (q as RenderQuality) : null;
  } catch {
    return null;
  }
}

/** Frames per auto-quality decision window. */
const AUTO_WINDOW = 90;
/** Below this average fps we step down a tier. */
const AUTO_DOWN_FPS = 52;
/** Comfortably above the down threshold — the gap is the hysteresis band. */
const AUTO_UP_FPS = 88;
/**
 * On a 60Hz panel vsync caps us at 60, so a fixed 88fps gate would mean the renderer could
 * never climb back after a downgrade. Instead we also accept "as fast as this machine has
 * ever gone", which is a decent proxy for the refresh cap.
 */
const AUTO_UP_VSYNC_FRACTION = 0.97;
const AUTO_UP_MIN_FPS = 58;
const AUTO_UP_WINDOWS = 4;
/** Stop climbing after this many up-then-down cycles, so we can never oscillate forever. */
const AUTO_MAX_UPGRADES = 2;

const _savedPos = new THREE.Vector3();
const _savedQuat = new THREE.Quaternion();
const _camForward = new THREE.Vector3();
const _sunPoint = new THREE.Vector3();

/**
 * How far off-axis the sun may be before the light-shaft march is switched off.
 *
 * Two reasons, and they happen to agree. Physically, a shaft you are looking away from is not
 * a shaft. Numerically, projecting a point at 90 degrees to the view direction divides by a
 * vanishing w, so the sun's screen position runs away to infinity exactly there — fading the
 * effect out well before that keeps the march sampling somewhere meaningful.
 */
const SHAFT_FACING_MIN = 0.12;
const SHAFT_FACING_FULL = 0.55;
/** A point far enough along the sun ray to project as "the sun", well outside the arena. */
const SUN_PROJECTION_DISTANCE = 1000;

/**
 * How fast HIGH NOON ramps in and out. Slow — the sun visibly swinging down and the frame
 * hardening over about two seconds reads as the light changing; snapping it in one frame would
 * read as a filter being switched on, which is exactly the failure the amber wash had.
 */
const NOON_DAMP = 1.6;

/** Smooth, deterministic, zero-allocation wobble. Three incommensurate sines never repeat. */
function wobble(t: number): number {
  return Math.sin(t * 1.0) * 0.55 + Math.sin(t * 2.37 + 1.7) * 0.3 + Math.sin(t * 4.11 + 4.1) * 0.15;
}

export class Renderer {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly gl: THREE.WebGLRenderer;
  readonly lighting: LightingRig;
  readonly sky: Sky;

  /** Turn off to pin the quality tier manually. */
  autoQuality = true;

  private readonly container: HTMLElement;
  private readonly postfx: PostFX;
  private readonly resizeObserver: ResizeObserver | null;
  private readonly onWindowResize: () => void;
  private readonly onContextLost: (e: Event) => void;

  private _quality: RenderQuality = 'high';
  private ceilingQuality: RenderQuality = 'high';

  private elapsed = 0;
  private trauma = 0;
  private rollTarget = 0;
  private roll = 0;
  private fovTarget = FOV_BASE;
  private noonTarget = 0;
  private noon = 0;

  private windowFrames = 0;
  private windowTime = 0;
  private settleFrames = AUTO_WINDOW;
  private cleanWindows = 0;
  private peakFps = 0;
  private upgradesUsed = 0;

  private disposed = false;

  constructor(container: HTMLElement) {
    this.container = container;

    this.gl = new THREE.WebGLRenderer({
      antialias: false, // FXAA runs in the post chain, after tone mapping, where it belongs
      alpha: false,
      stencil: false,
      depth: true,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    this.gl.setClearColor(new THREE.Color().setHex(PALETTE.bg, THREE.SRGBColorSpace), 1);
    // Clear alpha MUST be 1: alpha is the inverse selective-bloom mask, so an unwritten
    // pixel has to read as "never bloom".
    this.gl.setClearAlpha(1);
    this.gl.outputColorSpace = THREE.SRGBColorSpace;
    this.gl.toneMapping = THREE.NoToneMapping; // the ink composite tonemaps instead
    this.gl.shadowMap.enabled = true;
    // PCF, not PCFSoft. PCFSoftShadowMap ignores `shadow.radius` and applies a fixed wide
    // blur, which turns the one lighting event in the frame that has a SHAPE into a smudge.
    // DESIGN.md 4 asks for a crisp terminator; this is where that is decided.
    this.gl.shadowMap.type = THREE.PCFShadowMap;
    this.gl.domElement.style.display = 'block';
    this.gl.domElement.style.width = '100%';
    this.gl.domElement.style.height = '100%';
    container.appendChild(this.gl.domElement);

    this.onContextLost = (e: Event) => {
      // Let the browser restore the context instead of tearing the page down.
      e.preventDefault();
    };
    this.gl.domElement.addEventListener('webglcontextlost', this.onContextLost, false);

    this.scene = new THREE.Scene();
    this.scene.name = 'OneTap';

    this.camera = new THREE.PerspectiveCamera(FOV_BASE, 1, 0.05, CAMERA_FAR);
    this.camera.name = 'ViewCamera';
    this.camera.rotation.order = 'YXZ';
    this.scene.add(this.camera);

    this.lighting = createLighting(this.scene);
    // One sun, three consumers. The lighting rig owns the direction; the sky draws the disc
    // there and the toon grade wraps its azimuth around corners, so they cannot drift apart.
    setToonSunDirection(this.lighting.sunDirection);
    // Same contract for the cascade: the rig owns how far its shadows actually reach, and the
    // toon grade fades them out just inside that, so the frustum edge is never a visible seam.
    setToonShadowFade(this.lighting.shadowFade);

    this.sky = createSky();
    this.sky.setSunDirection(this.lighting.sunDirection);
    this.scene.add(this.sky.object);

    this.scene.fog = new THREE.Fog(this.sky.horizonColor.clone(), FOG_NEAR, FOG_FAR);

    this.postfx = createPostFX(this.gl, this.scene, this.camera);

    // `?quality=low|medium|high` pins the quality tier and disables the auto governor.
    // Without this, any automated capture is at the mercy of whatever tier the governor
    // happened to settle on, so two screenshots of identical code can disagree — which is
    // exactly how a correct lighting rig got reported as having no shadows, twice.
    // A reviewer (human or agent) needs to be able to state which tier they judged.
    const pinned = readPinnedQuality();
    if (pinned) {
      this.autoQuality = false;
      this.applyQuality(pinned);
    } else {
      this.applyQuality('high');
    }
    this.resize();

    this.onWindowResize = () => this.resize();
    window.addEventListener('resize', this.onWindowResize, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(container);
    } else {
      this.resizeObserver = null;
    }
  }

  // -------------------------------------------------------------------------
  // Quality
  // -------------------------------------------------------------------------

  get quality(): RenderQuality {
    return this._quality;
  }

  /** Pin a tier. Also raises the ceiling that auto-quality is allowed to climb back to. */
  set quality(q: RenderQuality) {
    this.ceilingQuality = q;
    this.applyQuality(q);
  }

  private applyQuality(q: RenderQuality): void {
    this._quality = q;
    this.postfx.setQuality(q);
    const shadowSize = SHADOW_SIZE_BY_QUALITY[q];
    this.lighting.setShadows(shadowSize > 0, shadowSize > 0 ? shadowSize : SHADOW_MAP_SIZE);
    this.gl.shadowMap.enabled = shadowSize > 0;
    this.gl.shadowMap.needsUpdate = true;
    this.settleFrames = AUTO_WINDOW;
    this.cleanWindows = 0;
    this.resize();
  }

  private stepQuality(dir: -1 | 1): void {
    const i = QUALITY_ORDER.indexOf(this._quality);
    const ceiling = QUALITY_ORDER.indexOf(this.ceilingQuality);
    const next = clamp(i + dir, 0, Math.min(QUALITY_ORDER.length - 1, ceiling));
    if (next === i) return;
    this.applyQuality(QUALITY_ORDER[next]!);
  }

  // -------------------------------------------------------------------------
  // Sizing
  // -------------------------------------------------------------------------

  resize(): void {
    if (this.disposed) return;
    const w = Math.max(1, this.container.clientWidth || window.innerWidth);
    const h = Math.max(1, this.container.clientHeight || window.innerHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, DPR_BY_QUALITY[this._quality]);

    this.gl.setPixelRatio(dpr);
    this.gl.setSize(w, h, false);

    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();

    this.postfx.setSize(w, h, dpr);
    setToonPixelRatio(dpr);
    setOutlineResolution(w * dpr, h * dpr, dpr);
    this.sky.setPixelRatio(dpr);
  }

  // -------------------------------------------------------------------------
  // Feel channels
  // -------------------------------------------------------------------------

  /**
   * 0 = standing still, 1 = flat out. Drives FOV-independent speed feedback: chromatic
   * aberration, the radial blur and the peripheral wind streaks.
   */
  setSpeed01(v: number): void {
    this.postfx.setSpeed01(v);
  }

  /** Add trauma. Small taps (a shot) stack; big ones (a hard landing) saturate. */
  addShake(amount: number): void {
    this.trauma = clamp(this.trauma + amount, 0, 1);
  }

  /** Two-frame full-screen ink splat in the given colour. */
  inkSplash(colorHex: number): void {
    this.postfx.splash(colorHex);
  }

  /**
   * HIGH NOON (DESIGN.md 2) — sudden death. Call with `1` while `match.suddenDeath` is set and
   * `0` otherwise; the ramp is damped internally.
   *
   * THIS IS A LIGHTING STATE, NOT A COLOUR FILTER. It drops the sun toward the horizon so every
   * shadow in the arena lengthens and deepens, hardens the frame's contrast with a gamma, and
   * lets the sun start to burn through bloom and shafts. It deliberately does NOT tint anything:
   * the previous amber sepia wash spent the whole colour budget on a state change and took the
   * team colours and the amber "who is armed" glow down with it, in the one minute of the round
   * where those two reads matter most (DESIGN.md 4).
   */
  setHighNoon(on: boolean | number): void {
    const t = typeof on === 'boolean' ? (on ? 1 : 0) : on;
    this.noonTarget = clamp(t, 0, 1);
  }

  /** Target camera roll in radians (wall-running, strafing lean). Damped internally. */
  setCameraRoll(rad: number): void {
    this.rollTarget = rad;
  }

  setTargetFov(deg: number): void {
    this.fovTarget = clamp(deg, 30, 140);
  }

  // -------------------------------------------------------------------------
  // Frame
  // -------------------------------------------------------------------------

  render(dtSec: number): void {
    if (this.disposed) return;
    const dt = dtSec > 0.1 ? 0.1 : dtSec < 0 ? 0 : dtSec;
    this.elapsed += dt;

    // --- FOV
    const fov = damp(this.camera.fov, this.fovTarget, FOV_DAMP_RATE, dt);
    if (Math.abs(fov - this.camera.fov) > 0.002) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }

    // --- HIGH NOON. Damped here and pushed to both consumers, because the two halves of it —
    //     a lower sun and a harder grade — have to arrive together or the frame contrast will
    //     visibly lead the shadows.
    if (this.noon !== this.noonTarget) {
      this.noon = damp(this.noon, this.noonTarget, NOON_DAMP, dt);
      if (Math.abs(this.noon - this.noonTarget) < 0.002) this.noon = this.noonTarget;
      this.lighting.setHighNoon(this.noon);
      this.postfx.setHighNoon(this.noon);
    }

    // --- roll + trauma
    this.roll = damp(this.roll, this.rollTarget, CAMERA_ROLL_DAMP, dt);
    if (this.trauma > 0) {
      this.trauma *= Math.exp(-SHAKE_DECAY * dt);
      if (this.trauma < 0.0015) this.trauma = 0;
    }

    // The caller owns the camera transform; we layer roll and shake on top and put it
    // back afterwards so nothing accumulates frame to frame.
    _savedPos.copy(this.camera.position);
    _savedQuat.copy(this.camera.quaternion);

    const shake = Math.pow(this.trauma, SHAKE_CURVE);
    if (shake > 0) {
      const t = this.elapsed * SHAKE_FREQ;
      const a = SHAKE_MAX_ANGLE * shake;
      this.camera.rotateY(wobble(t) * a);
      this.camera.rotateX(wobble(t + 17.31) * a);
      this.camera.rotateZ(wobble(t + 41.77) * a * 0.7);
      const o = SHAKE_MAX_OFFSET * shake;
      this.camera.translateX(wobble(t + 63.19) * o);
      this.camera.translateY(wobble(t + 91.53) * o);
    }
    if (this.roll !== 0) this.camera.rotateZ(this.roll);

    this.camera.updateMatrixWorld(true);

    // --- per-frame uniforms
    updateToonTime(this.elapsed);
    // The sun's elevation moves at HIGH NOON, and the sky caches its direction in a uniform.
    // Re-pushing every frame is a copy-and-normalise of one Vector3 and removes any chance of
    // the drawn disc drifting out of line with the shadows it is supposed to be casting.
    this.sky.setSunDirection(this.lighting.sunDirection);
    this.sky.update(this.camera, this.elapsed);
    this.lighting.update(this.camera);
    this.updateSunScreen();

    this.postfx.render(dt, this.elapsed);

    this.camera.position.copy(_savedPos);
    this.camera.quaternion.copy(_savedQuat);
    this.camera.updateMatrixWorld(true);

    this.tickAutoQuality(dt);
  }

  /**
   * Project the sun into screen space for the composite's light-shaft march and the HIGH NOON
   * glare.
   *
   * On an open rooftop the sun is genuinely in frame a lot of the time, so this is no longer
   * only feeding an indirect effect: look up-sun and the march finds sky between every parapet,
   * mast and vent stack and draws real god rays off their edges. Which is the point — nothing
   * says "outdoors, and it is sunny" faster than a shaft.
   */
  private updateSunScreen(): void {
    this.camera.getWorldDirection(_camForward);
    const facing = _camForward.dot(this.lighting.sunDirection);
    if (facing <= SHAFT_FACING_MIN) {
      this.postfx.setSunScreen(0.5, 1.2, 0);
      return;
    }
    _sunPoint
      .copy(this.camera.position)
      .addScaledVector(this.lighting.sunDirection, SUN_PROJECTION_DISTANCE)
      .project(this.camera);
    const k = clamp((facing - SHAFT_FACING_MIN) / (SHAFT_FACING_FULL - SHAFT_FACING_MIN), 0, 1);
    this.postfx.setSunScreen(_sunPoint.x * 0.5 + 0.5, _sunPoint.y * 0.5 + 0.5, k * k * (3 - 2 * k));
  }

  private tickAutoQuality(dt: number): void {
    if (!this.autoQuality) return;
    if (this.settleFrames > 0) {
      this.settleFrames--;
      return;
    }
    this.windowFrames++;
    this.windowTime += dt;
    if (this.windowFrames < AUTO_WINDOW) return;

    const fps = this.windowTime > 0 ? this.windowFrames / this.windowTime : 60;
    this.windowFrames = 0;
    this.windowTime = 0;

    if (fps > this.peakFps) this.peakFps = fps;

    if (fps < AUTO_DOWN_FPS) {
      this.cleanWindows = 0;
      this.stepQuality(-1);
      return;
    }

    const headroom =
      fps > AUTO_UP_FPS ||
      (fps >= AUTO_UP_MIN_FPS && fps >= this.peakFps * AUTO_UP_VSYNC_FRACTION);

    if (headroom && this.upgradesUsed < AUTO_MAX_UPGRADES) {
      this.cleanWindows++;
      if (this.cleanWindows >= AUTO_UP_WINDOWS) {
        this.cleanWindows = 0;
        const before = this._quality;
        this.stepQuality(1);
        if (this._quality !== before) this.upgradesUsed++;
      }
    } else {
      this.cleanWindows = 0;
    }
  }

  // -------------------------------------------------------------------------

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    window.removeEventListener('resize', this.onWindowResize);
    if (this.resizeObserver !== null) this.resizeObserver.disconnect();
    this.gl.domElement.removeEventListener('webglcontextlost', this.onContextLost);

    this.postfx.dispose();
    this.sky.dispose();
    this.lighting.dispose();
    disposeToonShared();

    this.scene.clear();
    this.gl.dispose();
    if (this.gl.domElement.parentNode !== null) {
      this.gl.domElement.parentNode.removeChild(this.gl.domElement);
    }
  }
}
